/**
 * Safe Storage Service
 * Provides robust storage operations with versioning, migration, and error handling
 * for localStorage, sessionStorage, and cookies
 */

import { STORAGE_VERSION } from './versionService';

// Type for storage backends
type StorageBackend = 'localStorage' | 'sessionStorage' | 'cookie';

// Schema version tracking per key
interface StorageEntry<T> {
  version: number;
  data: T;
  timestamp: number;
}

// Migration function type
type MigrationFn<T> = (oldData: unknown, oldVersion: number) => T;

// Migration registry
const migrations: Map<string, Map<number, MigrationFn<unknown>>> = new Map();

/**
 * Register a migration function for a storage key
 */
export const registerMigration = <T>(
  key: string, 
  fromVersion: number, 
  migrationFn: MigrationFn<T>
): void => {
  if (!migrations.has(key)) {
    migrations.set(key, new Map());
  }
  migrations.get(key)!.set(fromVersion, migrationFn as MigrationFn<unknown>);
};

/**
 * Apply migrations to data from old version to current version
 */
const applyMigrations = <T>(key: string, data: unknown, fromVersion: number): T => {
  let currentData = data;
  let currentVersion = fromVersion;
  const keyMigrations = migrations.get(key);
  
  if (!keyMigrations) {
    return currentData as T;
  }
  
  // Apply migrations in order
  while (currentVersion < STORAGE_VERSION) {
    const migration = keyMigrations.get(currentVersion);
    if (migration) {
      try {
        currentData = migration(currentData, currentVersion);
        currentVersion++;
      } catch (e) {
        console.warn(`Migration failed for ${key} from version ${currentVersion}:`, e);
        return currentData as T;
      }
    } else {
      // No migration for this version, skip
      currentVersion++;
    }
  }
  
  return currentData as T;
};

/**
 * Check if localStorage is available
 */
const isLocalStorageAvailable = (): boolean => {
  try {
    const test = '__storage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
};

/**
 * Check if sessionStorage is available
 */
const isSessionStorageAvailable = (): boolean => {
  try {
    const test = '__storage_test__';
    sessionStorage.setItem(test, test);
    sessionStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
};

/**
 * Safely get item from localStorage with versioning support
 */
export const safeGetItem = <T>(
  key: string, 
  defaultValue: T, 
  backend: StorageBackend = 'localStorage'
): T => {
  try {
    let raw: string | null = null;
    
    switch (backend) {
      case 'localStorage':
        if (!isLocalStorageAvailable()) return defaultValue;
        raw = localStorage.getItem(key);
        break;
      case 'sessionStorage':
        if (!isSessionStorageAvailable()) return defaultValue;
        raw = sessionStorage.getItem(key);
        break;
      case 'cookie':
        raw = getCookieRaw(key);
        break;
    }
    
    if (!raw) return defaultValue;
    
    // Try to parse as versioned entry
    const parsed = JSON.parse(raw);
    
    // Check if it's a versioned entry
    if (parsed && typeof parsed === 'object' && 'version' in parsed && 'data' in parsed) {
      const entry = parsed as StorageEntry<T>;
      
      // Apply migrations if needed
      if (entry.version < STORAGE_VERSION) {
        const migratedData = applyMigrations<T>(key, entry.data, entry.version);
        // Re-save with current version
        safeSetItem(key, migratedData, backend);
        return migratedData;
      }
      
      return entry.data;
    }
    
    // Legacy data without versioning - migrate to versioned format
    console.log(`Migrating legacy data for key: ${key}`);
    const migratedData = applyMigrations<T>(key, parsed, 0);
    safeSetItem(key, migratedData, backend);
    return migratedData;
    
  } catch (e) {
    console.warn(`Failed to read ${key} from ${backend}:`, e);
    // If parsing fails, clear corrupted data and return default
    safeRemoveItem(key, backend);
    return defaultValue;
  }
};

/**
 * Safely set item in storage with versioning
 */
export const safeSetItem = <T>(
  key: string, 
  value: T, 
  backend: StorageBackend = 'localStorage',
  options?: { expires?: number } // expires in days for cookies
): boolean => {
  try {
    const entry: StorageEntry<T> = {
      version: STORAGE_VERSION,
      data: value,
      timestamp: Date.now()
    };
    
    const serialized = JSON.stringify(entry);
    
    switch (backend) {
      case 'localStorage':
        if (!isLocalStorageAvailable()) return false;
        localStorage.setItem(key, serialized);
        break;
      case 'sessionStorage':
        if (!isSessionStorageAvailable()) return false;
        sessionStorage.setItem(key, serialized);
        break;
      case 'cookie':
        setCookieRaw(key, serialized, options?.expires || 365);
        break;
    }
    
    return true;
  } catch (e) {
    console.warn(`Failed to write ${key} to ${backend}:`, e);
    
    // Handle quota exceeded errors
    if (e instanceof DOMException && 
        (e.code === 22 || e.code === 1014 || e.name === 'QuotaExceededError')) {
      console.warn('Storage quota exceeded, attempting cleanup...');
      cleanupOldEntries(backend);
      // Retry once after cleanup
      try {
        const entry: StorageEntry<T> = {
          version: STORAGE_VERSION,
          data: value,
          timestamp: Date.now()
        };
        const serialized = JSON.stringify(entry);
        if (backend === 'localStorage') {
          localStorage.setItem(key, serialized);
          return true;
        } else if (backend === 'sessionStorage') {
          sessionStorage.setItem(key, serialized);
          return true;
        }
      } catch {
        return false;
      }
    }
    
    return false;
  }
};

/**
 * Safely remove item from storage
 */
export const safeRemoveItem = (
  key: string, 
  backend: StorageBackend = 'localStorage'
): boolean => {
  try {
    switch (backend) {
      case 'localStorage':
        if (!isLocalStorageAvailable()) return false;
        localStorage.removeItem(key);
        break;
      case 'sessionStorage':
        if (!isSessionStorageAvailable()) return false;
        sessionStorage.removeItem(key);
        break;
      case 'cookie':
        deleteCookie(key);
        break;
    }
    return true;
  } catch (e) {
    console.warn(`Failed to remove ${key} from ${backend}:`, e);
    return false;
  }
};

/**
 * Clear all app-related storage entries
 */
export const clearAllAppStorage = (): void => {
  const appPrefixes = ['legal_prompt_', 'app_version', 'storage_version'];
  
  // Clear localStorage
  if (isLocalStorageAvailable()) {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && appPrefixes.some(prefix => key.startsWith(prefix))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  }
  
  // Clear sessionStorage
  if (isSessionStorageAvailable()) {
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && appPrefixes.some(prefix => key.startsWith(prefix))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
  }
  
  // Clear cookies
  appPrefixes.forEach(prefix => {
    document.cookie.split(';').forEach(cookie => {
      const [name] = cookie.split('=').map(c => c.trim());
      if (name.startsWith(prefix)) {
        deleteCookie(name);
      }
    });
  });
};

/**
 * Clean up old entries to free space
 */
const cleanupOldEntries = (backend: StorageBackend): void => {
  if (backend === 'cookie') return;
  
  const storage = backend === 'localStorage' ? localStorage : sessionStorage;
  const entries: Array<{ key: string; timestamp: number }> = [];
  
  try {
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (!key) continue;
      
      try {
        const raw = storage.getItem(key);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed === 'object' && 'timestamp' in parsed) {
            entries.push({ key, timestamp: parsed.timestamp });
          }
        }
      } catch {
        // Skip non-JSON entries
      }
    }
    
    // Sort by timestamp and remove oldest 25%
    entries.sort((a, b) => a.timestamp - b.timestamp);
    const toRemove = entries.slice(0, Math.ceil(entries.length * 0.25));
    toRemove.forEach(({ key }) => storage.removeItem(key));
  } catch (e) {
    console.warn('Failed to cleanup old entries:', e);
  }
};

// Cookie helpers
const getCookieRaw = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return decodeURIComponent(parts.pop()?.split(';').shift() || '');
  }
  return null;
};

const setCookieRaw = (name: string, value: string, days: number): void => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Strict`;
};

const deleteCookie = (name: string): void => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Strict`;
};

/**
 * Validate data against a schema
 */
export const validateData = <T>(
  data: unknown, 
  validator: (d: unknown) => d is T,
  defaultValue: T
): T => {
  try {
    if (validator(data)) {
      return data;
    }
  } catch (e) {
    console.warn('Data validation failed:', e);
  }
  return defaultValue;
};

/**
 * Create a typed storage accessor for a specific key
 */
export const createStorageAccessor = <T>(
  key: string,
  defaultValue: T,
  backend: StorageBackend = 'localStorage',
  validator?: (d: unknown) => d is T
) => ({
  get: (): T => {
    const value = safeGetItem<T>(key, defaultValue, backend);
    if (validator) {
      return validateData(value, validator, defaultValue);
    }
    return value;
  },
  set: (value: T, options?: { expires?: number }): boolean => {
    if (validator && !validator(value)) {
      console.warn(`Invalid data for ${key}, not saving`);
      return false;
    }
    return safeSetItem(key, value, backend, options);
  },
  remove: (): boolean => safeRemoveItem(key, backend),
});
